import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SetURLSearchParams } from 'react-router';
import { addMonths, isDay, monthDays, periodOfLocal } from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * La période, lue dans l'URL et partagée par les deux vues (SYN-02, VUE-16).
 *
 * `du` et `au` sont des jours `AAAA-MM-JJ`, le second compris. L'ancien `period=AAAA-MM`
 * — le seul filtre de dates jusqu'au 24.09.2026 — reste lu : un lien envoyé avant doit
 * rouvrir le même écran (VUE-03). Il est converti en « du 1ᵉʳ au dernier jour » et
 * disparaît de l'URL dès qu'on touche aux dates.
 *
 * Un jour invalide dans l'URL retombe sur « pas de borne », en silence.
 */
export function useDateRange(params: URLSearchParams, setParams: SetURLSearchParams) {
  const du = params.get('du');
  const au = params.get('au');
  const legacy = params.get('period');

  const range = useMemo(() => {
    const from = du && isDay(du) ? du : null;
    const to = au && isDay(au) ? au : null;
    if (from || to) return { from, to };
    if (legacy && MONTH.test(legacy)) return monthDays(legacy);
    return { from: null, to: null };
  }, [du, au, legacy]);

  /** Le mois exactement couvert par la période, s'il y en a un — pour la frise. */
  const month = useMemo(() => {
    if (!range.from || !range.to || !range.from.endsWith('-01')) return null;
    const period = range.from.slice(0, 7);
    return monthDays(period).to === range.to ? period : null;
  }, [range]);

  const setRange = useCallback(
    (from: string | null, to: string | null) => {
      const next = new URLSearchParams(params);
      next.delete('period');
      if (from) next.set('du', from);
      else next.delete('du');
      if (to) next.set('au', to);
      else next.delete('au');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  return { from: range.from, to: range.to, month, setRange };
}

/** Un jour lisible, à la manière suisse de la langue — sans fuseau qui le décalerait. */
export function useDayFormat(locale: Locale) {
  return useMemo(() => {
    const format = new Intl.DateTimeFormat(locale === 'de' ? 'de-CH' : 'fr-CH', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      // Un jour du calendrier n'a pas d'heure : lu à midi UTC, il ne change jamais de date.
      timeZone: 'UTC',
    });
    return (day: string) => format.format(new Date(`${day}T12:00:00Z`));
  }, [locale]);
}

/** La période en toutes lettres, pour la barre des filtres actifs. */
export function describeRange(
  t: T,
  day: (d: string) => string,
  from: string | null,
  to: string | null,
): string | null {
  if (from && to) return t('range.between', { from: day(from), to: day(to) });
  if (from) return t('range.since', { date: day(from) });
  if (to) return t('range.until', { date: day(to) });
  return null;
}

/** Le libellé court du bouton : « août 2026 », « 01.08.2026 – 15.09.2026 », « Toute la période ». */
function rangeLabel(
  t: T,
  locale: Locale,
  day: (d: string) => string,
  from: string | null,
  to: string | null,
): string {
  if (from && to && from.endsWith('-01') && monthDays(from.slice(0, 7)).to === to) {
    return new Intl.DateTimeFormat(locale === 'de' ? 'de-CH' : 'fr-CH', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${from}T12:00:00Z`));
  }
  if (from && to) return `${day(from)} – ${day(to)}`;
  if (from) return t('range.since', { date: day(from) });
  if (to) return t('range.until', { date: day(to) });
  return t('range.allTime');
}

/**
 * La période, en un bouton : un clic ouvre les raccourcis — ce mois, le mois passé, les
 * trois derniers — et les deux jours « du … au … ». Refermé, il ne prend qu'une place de
 * champ dans la barre, et dit la période choisie en toutes lettres (« août 2026 »).
 *
 * Échap et un clic ailleurs referment (Charte §6), comme le menu de la roue dentée.
 */
export function PeriodPicker({
  t,
  locale,
  from,
  to,
  onChange,
}: {
  t: T;
  locale: Locale;
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const day = useDayFormat(locale);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // « Ce mois » à l'heure suisse : le 1ᵉʳ à 0 h 30, c'est déjà le nouveau mois.
  // « Les N derniers mois » : le mois en cours et les N − 1 précédents, du 1ᵉʳ au dernier
  // jour — les dates exactes s'affichent juste dessous, dans « du … au … ».
  const presets = useMemo(() => {
    const current = periodOfLocal(new Date());
    const lastMonths = (n: number) => ({
      from: monthDays(addMonths(current, -(n - 1))).from,
      to: monthDays(current).to,
    });
    return [
      { key: 'range.thisMonth' as const, ...monthDays(current) },
      { key: 'range.lastMonth' as const, ...monthDays(addMonths(current, -1)) },
      { key: 'range.last3' as const, ...lastMonths(3) },
      // 6 et 12 mois : le rythme auquel se facturent les copies privées (24.09.2026).
      { key: 'range.last6' as const, ...lastMonths(6) },
      { key: 'range.last12' as const, ...lastMonths(12) },
      { key: 'range.all' as const, from: null, to: null },
    ];
  }, []);

  // Une borne posée après l'autre les échange plutôt que de rendre une période vide.
  const set = (nextFrom: string | null, nextTo: string | null) => {
    if (nextFrom && nextTo && nextFrom > nextTo) onChange(nextTo, nextFrom);
    else onChange(nextFrom, nextTo);
  };

  const active = Boolean(from || to);

  // « 12 derniers mois » dit mieux que « 01.10.2025 – 30.09.2026 » ce qu'on a choisi. Un
  // mois entier garde son nom (« août 2026 »), plus parlant que « mois passé ».
  const matched = presets.find(
    (p) =>
      (p.key === 'range.last3' || p.key === 'range.last6' || p.key === 'range.last12') &&
      p.from === from &&
      p.to === to,
  );
  const label = matched ? t(matched.key) : rangeLabel(t, locale, day, from, to);

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${t('filter.period')} : ${label}`}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm transition-colors hover:bg-bg ${
          active ? 'border-accent bg-accent/10 font-medium text-fg' : 'border-border text-fg'
        }`}
      >
        <Icon name="calendar" />
        <span className="whitespace-nowrap first-letter:uppercase">{label}</span>
        <Icon name="chevron" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('filter.period')}
          className="absolute left-0 z-20 mt-2 w-[min(22rem,calc(100vw-2rem))] space-y-3 rounded-xl border border-card-border bg-surface p-3 shadow-lg"
        >
          <div className="grid grid-cols-2 gap-1.5">
            {presets.map((p) => {
              const selected = from === p.from && to === p.to;
              return (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    onChange(p.from, p.to);
                    setOpen(false);
                  }}
                  className={`min-h-[44px] rounded-lg border px-3 text-left text-sm transition-colors ${
                    selected
                      ? 'border-accent bg-accent/15 font-semibold text-fg'
                      : 'border-border text-fg hover:bg-bg'
                  }`}
                >
                  {t(p.key)}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2 border-t border-border pt-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">{t('range.from')}</span>
              <input
                type="date"
                className="field w-full"
                value={from ?? ''}
                onChange={(e) => set(isDay(e.target.value) ? e.target.value : null, to)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">{t('range.to')}</span>
              <input
                type="date"
                className="field w-full"
                value={to ?? ''}
                onChange={(e) => set(from, isDay(e.target.value) ? e.target.value : null)}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/** Les quelques icônes de la barre — trait fin, héritent de la couleur du texte. */
export function Icon({
  name,
  className = '',
}: {
  name: 'calendar' | 'chevron' | 'search' | 'download' | 'reset';
  className?: string;
}) {
  const paths: Record<typeof name, string> = {
    calendar:
      'M8 3v3M16 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
    chevron: 'm6 9 6 6 6-6',
    search: 'm20 20-4.2-4.2M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14z',
    download: 'M12 4v11m0 0-4-4m4 4 4-4M5 20h14',
    reset: 'M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4',
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`h-4 w-4 shrink-0 ${className}`}
    >
      <path d={paths[name]} />
    </svg>
  );
}
